import { useState } from 'react';
import type { To } from 'react-router';
import { Link, NavLink } from 'react-router';
import type { SideNavigationItem, SideNavigationItemProps } from './navigation-items.interfaces';
import { SideNavigationItemsEnum } from './navigation-items.interfaces';
import clsx from 'clsx';
import NavigationItemInnerElement from './navigation-item-inner-element.component';
import { useScreenSize } from '../../common-hooks';

interface NavigationItemProps {
  item: SideNavigationItem;
  index: number;
  isItemCollapsed: boolean;
  isMobile: boolean;
}

interface NavigationItems {
  items: SideNavigationItem[];
  isItemCollapsed: boolean;
}

function NavigationItem({ item, index, isItemCollapsed, isMobile }: NavigationItemProps) {
  const [expandedSubItems, setExpandedSubItems] = useState<boolean>(false);

  const handleSubItemsExpanded = (item: SideNavigationItem) => {
    if (item && item.subItems && item.subItems.length > 0) {
      item?.setCollapsedSubItems?.(!expandedSubItems);
      setExpandedSubItems(!expandedSubItems);
    }
  };

  const itemsWrapper =
    'odin-flex odin-items-center odin-py-2 odin-transition-colors odin-duration-300 odin-border-l-4 odin-border-transparent odin-relative hover:odin-bg-primary-4 dark:odin-hover:bg-gray-3';

  function navigationItemsRenderer(
    item: SideNavigationItemProps,
    index: number,
    navigationItem: SideNavigationItemsEnum
  ) {
    const isParentItem = navigationItem === SideNavigationItemsEnum.ParentNavigationItem;
    const isSubItem = navigationItem === SideNavigationItemsEnum.SubNavigationItem;

    const subNavigationItemWrapper = clsx(itemsWrapper, { 'odin-pl-10': isSubItem });

    if (item) {
      if (item.target) {
        return (
          <Link
            key={index}
            to={item.to as To}
            target={item.target}
            className={subNavigationItemWrapper}
            onClick={() => item.onClick?.()}
          >
            <NavigationItemInnerElement item={item} isItemCollapsed={isItemCollapsed} isMobile={isMobile} />
          </Link>
        );
      } else {
        return (
          <NavLink
            key={index}
            to={item.to as To}
            state={item.state}
            target={item.target}
            className={({ isActive }) =>
              clsx(subNavigationItemWrapper, {
                'odin-bg-primary-5 !odin-border-primary-1 odin-text-primary-1 odin-font-semibold hover:!odin-bg-primary-5':
                  isActive,
              })
            }
            onClick={(e) => {
              const target = e.target as HTMLElement;
              if (isMobile && target.closest('#badge-clickable')) {
                e.preventDefault();
              } else {
                item.onClick?.();
              }
            }}
          >
            <NavigationItemInnerElement
              item={item}
              isItemCollapsed={isItemCollapsed}
              isParentItem={isParentItem}
              expandedSubItems={expandedSubItems}
              isMobile={isMobile}
              handleSubItemsExpanded={handleSubItemsExpanded}
            />
          </NavLink>
        );
      }
    }
  }

  return (
    item && (
      <>
        {navigationItemsRenderer(item, index, SideNavigationItemsEnum.ParentNavigationItem)}
        {item.subItems && item.subItems.length > 0 && (
          <div className="odin-ease odin-flex-col odin-overflow-hidden odin-transition-all">
            {expandedSubItems &&
              item.subItems.map((subItem, index) =>
                navigationItemsRenderer(subItem, index, SideNavigationItemsEnum.SubNavigationItem)
              )}
          </div>
        )}
      </>
    )
  );
}

export default function NavigationItems({ items, isItemCollapsed }: NavigationItems) {
  const { isMobile } = useScreenSize();

  return (
    <>
      {items?.map((item, index) => (
        <NavigationItem
          key={`${item.label}-${index}`}
          item={item}
          index={index}
          isItemCollapsed={isItemCollapsed}
          isMobile={isMobile}
        />
      ))}
    </>
  );
}
